import { Database, GitMerge, Pin, Search, Sparkles } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/utils";

const TABS = ["Short-term", "Long-term", "Vector", "Episodic", "Semantic", "Procedural", "Emotional", "Quantum"];

const MEMORIES = [
  { type: "episodic", title: "Project kickoff with team", confidence: 0.94, time: "2h ago", emotion: "positive", tags: ["work", "team"], pinned: true },
  { type: "semantic", title: "Definition of constitutional AI", confidence: 0.99, time: "1d ago", emotion: "neutral", tags: ["concept", "ai-safety"] },
  { type: "vector", title: "Embedding cluster #842 (research)", confidence: 0.87, time: "6h ago", emotion: "neutral", tags: ["arxiv", "cluster"] },
  { type: "procedural", title: "How to spawn parallel agents", confidence: 0.96, time: "3h ago", emotion: "neutral", tags: ["workflow"] },
  { type: "emotional", title: "User frustration during deploy", confidence: 0.78, time: "12h ago", emotion: "negative", tags: ["feedback"], pinned: true },
  { type: "quantum", title: "Superposed plan (5 forks)", confidence: 0.71, time: "30m ago", emotion: "neutral", tags: ["simulation"] },
];

const COLORS: Record<string, string> = {
  episodic: "primary",
  semantic: "secondary",
  vector: "accent",
  procedural: "success",
  emotional: "warning",
  quantum: "info",
};

export function MemoryUniverse() {
  const [tab, setTab] = useState("Long-term");
  return (
    <div className="h-full overflow-y-auto p-6 space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            <Database className="w-6 h-6 text-accent" /> Memory Universe
          </h1>
          <p className="text-xs text-muted-foreground mt-1">14,284 memories · 4.2 GB compressed · self-evolving</p>
        </div>
        <div className="flex gap-2">
          <button className="text-xs px-3 py-1.5 rounded-md glass hover:border-primary/40 hover:text-primary flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Memory Audit Full
          </button>
          <button className="text-xs px-3 py-1.5 rounded-md glass hover:border-secondary/40 hover:text-secondary flex items-center gap-1.5">
            <GitMerge className="w-3 h-3" /> Merge & Compress
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 glass rounded-lg px-3 py-2">
        <Search className="w-4 h-4 text-muted-foreground" />
        <input
          placeholder="Semantic search across all memories..."
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        <span className="text-[10px] font-mono text-muted-foreground">vector + keyword + emotional</span>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={cn(
              "text-xs px-3 py-1.5 rounded-md font-mono transition-colors",
              tab === t
                ? "bg-gradient-neural text-primary-foreground shadow-glow-cyan"
                : "glass hover:border-primary/40 text-muted-foreground hover:text-foreground",
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Memory grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {MEMORIES.map((m, i) => {
          const color = COLORS[m.type];
          return (
            <div
              key={i}
              className="glass rounded-xl p-4 group hover:border-primary/40 transition-all hover:translate-y-[-2px] cursor-pointer relative overflow-hidden"
            >
              <div
                className="absolute top-0 left-0 right-0 h-0.5"
                style={{ background: `hsl(var(--${color}))`, boxShadow: `0 0 12px hsl(var(--${color}) / 0.6)` }}
              />
              <div className="flex items-start justify-between mb-2">
                <span
                  className="text-[10px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded"
                  style={{ background: `hsl(var(--${color}) / 0.15)`, color: `hsl(var(--${color}))` }}
                >
                  {m.type}
                </span>
                {m.pinned && <Pin className="w-3 h-3 text-warning fill-warning" />}
              </div>
              <h4 className="text-sm font-medium mb-3">{m.title}</h4>
              <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                <span>{m.time}</span>
                <span>conf: {Math.round(m.confidence * 100)}%</span>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {m.tags.map((tg) => (
                  <span key={tg} className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-surface-3 text-muted-foreground">
                    #{tg}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
